import { projectRole, type Project, type ProjectRole } from '@p2b/contract';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { ErrorNote } from '../../App.js';
import { api } from '../../api.js';
import { formatDay, label, useI18n } from '../../i18n.js';

export function AccessTab({ project }: { project: Project }) {
  const { t, lang } = useI18n();
  const qc = useQueryClient();
  const manage = (project.capabilities ?? []).includes('manage');
  const access = useQuery({ queryKey: ['access', project.id], queryFn: () => api.access(project.id) });
  const orgs = useQuery({ queryKey: ['orgs'], queryFn: () => api.orgs(), enabled: manage });
  const [form, setForm] = useState({
    type: 'email' as 'email' | 'org',
    id: '',
    role: 'client' as ProjectRole,
    notify: true,
  });
  const change = useMutation({
    mutationFn: (body: Parameters<typeof api.putAccess>[1]) => api.putAccess(project.id, body),
    onSuccess: (list) => {
      qc.setQueryData(['access', project.id], list);
      setForm((f) => ({ ...f, id: '' }));
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    change.mutate({ grant: [{ type: form.type, id: form.id, role: form.role }], notify: form.notify });
  };
  const roleOptions = projectRole.options.map((r) => (
    <option key={r} value={r}>
      {label(t, 'role', r)}
    </option>
  ));
  return (
    <div className="stack">
      <ErrorNote error={access.error ?? change.error} />
      <div className="sheet">
        <table className="stack-sm">
          <thead>
            <tr>
              <th>{t('who')}</th>
              <th>{t('role')}</th>
              <th className="hide-sm">{t('granted')}</th>
              {manage && <th />}
            </tr>
          </thead>
          <tbody>
            {(access.data ?? []).map((g) => (
              <tr key={`${g.principalType}:${g.principalId}`}>
                <td className="cell-full">
                  <div className="cell-strong break">{g.label}</div>
                  {g.principalType === 'org' && <span className="tag">{t('organization')}</span>}
                  {g.principalType === 'email' && <span className="tag">{t('notSignedIn')}</span>}
                </td>
                <td>
                  {manage ? (
                    <select
                      aria-label={t('role')}
                      value={g.role}
                      onChange={(e) =>
                        change.mutate({
                          grant: [
                            { type: g.principalType, id: g.principalId, role: e.target.value as ProjectRole },
                          ],
                        })
                      }
                    >
                      {roleOptions}
                    </select>
                  ) : (
                    label(t, 'role', g.role)
                  )}
                </td>
                <td className="hide-sm nowrap">{formatDay(g.createdAt, lang)}</td>
                {manage && (
                  <td className="actions">
                    <button
                      className="quiet danger"
                      onClick={() =>
                        change.mutate({ revoke: [{ type: g.principalType, id: g.principalId }] })
                      }
                    >
                      {t('revoke')}
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {manage && (
        <section className="section">
          <h2 className="section-title">{t('grant')}</h2>
          <form className="panel form-panel" onSubmit={submit}>
            <div className="form-grid">
              <label>
                {t('who')}
                <select
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value as 'email' | 'org', id: '' })}
                >
                  <option value="email">{t('person')}</option>
                  <option value="org">{t('organization')}</option>
                </select>
              </label>
              {form.type === 'email' ? (
                <label>
                  {t('email')}
                  <input
                    type="email"
                    required
                    value={form.id}
                    onChange={(e) => setForm({ ...form, id: e.target.value })}
                  />
                </label>
              ) : (
                <label>
                  {t('organization')}
                  <select required value={form.id} onChange={(e) => setForm({ ...form, id: e.target.value })}>
                    <option value="" />
                    {(orgs.data ?? []).map((o) => (
                      <option key={o.id} value={o.slug}>
                        {o.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label>
                {t('role')}
                <select
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value as ProjectRole })}
                >
                  {roleOptions}
                </select>
              </label>
            </div>
            {form.type === 'email' && (
              <label className="check">
                <input
                  type="checkbox"
                  checked={form.notify}
                  onChange={(e) => setForm({ ...form, notify: e.target.checked })}
                />
                {t('notify')}
              </label>
            )}
            <div>
              <button className="primary" disabled={change.isPending}>
                {t('grant')}
              </button>
            </div>
          </form>
        </section>
      )}
    </div>
  );
}
